import { h } from '@omni-torn/ui';
import { LEADERBOARD_KEEP, LEADERBOARD_SIZE, LEADERBOARD_TERMS, leaderboardDef, type LeaderboardAnswer, type LeaderboardIndex, type LeaderboardRow } from '@omni-torn/shared';
import { server } from '../session';
import { ago, box, duration, ext, loading, money, num, problem, profileUrl, short, table } from '../ui';
import { methodWord } from './estimate-view';

/**
 * Leaderboards (website roadmap S25, FF Scouter's leaderboards): top players by Omni-Torn's estimate, by public personal
 * stats (Torn's Hall of Fame and what Omni-Torn users on terms 13 saw), top factions, the most active players, and what
 * each public-stat board's players gained this quarter, last quarter or during one of Torn's events.
 * Data: GET /api/leaderboards and /api/leaderboards/:board (apps/api/src/leaderboards.ts — built once a day on the server;
 * this page asks Torn nothing).
 *
 * Address: /leaderboards?board=…&period=…&faction=… (kept as you pick, so a link opens the same board).
 */

const GROUPS: Array<{ label: string; kinds: string[] }> = [
  { label: 'Battle stats', kinds: ['estimate'] },
  { label: 'Public stats', kinds: ['stat'] },
  { label: 'Activity', kinds: ['activity'] },
  { label: 'Factions', kinds: ['faction'] },
];

const playerCell = (r: LeaderboardRow): HTMLElement => h('span', {}, h('a', { href: `/lookup/${r.id}`, 'data-link': true }, r.name ?? `[${r.id}]`), h('span', { class: 'muted small' }, ` [${r.id}] `), ext(profileUrl(r.id), '↗'));
const factionCell = (id: number | null, name?: string | null): HTMLElement | string => (id ? h('a', { href: `/factions/${id}`, 'data-link': true }, name ?? `[${id}]`) : '—');

function valueText(board: string, v: number): string {
  const d = leaderboardDef(board);
  if (d?.money) return money(v);
  if (d?.time) return duration(v);
  if (d?.kind === 'estimate' || board === 'f-estimate') return short(v);
  if (d?.kind === 'activity') return `${num(v, 1)} h`;
  return num(v);
}

/** Where a row's number came from, in words. */
function fromText(r: LeaderboardRow): string {
  if (r.src === 'hof') return r.place ? `Torn’s Hall of Fame #${num(r.place)} · ${ago(r.at)}` : `Torn’s Hall of Fame · ${ago(r.at)}`;
  if (r.src === 'seen') return `seen by an Omni-Torn user ${ago(r.at)}`;
  if (r.src === 'estimate') return r.method ? `from ${methodWord(r.method)} · ${ago(r.at)}` : 'Omni-Torn’s estimates';
  return `seen ${num(r.covered ?? 0, 1)} h of 168`;
}

export async function renderLeaderboards(view: HTMLElement): Promise<void> {
  const body = h('div', {}, loading('Reading the leaderboards…'));
  view.replaceChildren(h('div', { class: 'page-head' }, h('h1', {}, 'Leaderboards')), body);
  let idx: LeaderboardIndex;
  try {
    idx = await server<LeaderboardIndex>('GET', '/api/leaderboards');
  } catch (err) {
    body.replaceChildren(problem(err));
    return;
  }
  const q = new URLSearchParams(location.search);
  let board = idx.boards.some((b) => b.id === q.get('board')) ? q.get('board')! : 'estimate';
  let period = idx.periods.some((p) => p.id === q.get('period')) ? q.get('period')! : '';
  let faction = /^\d{1,9}$/.test(q.get('faction') ?? '') ? q.get('faction')! : '';

  const boardSel = h(
    'select',
    { 'aria-label': 'Leaderboard', 'data-lb': 'board' },
    ...GROUPS.map((g) => h('optgroup', { label: g.label }, ...idx.boards.filter((b) => g.kinds.includes(b.kind)).map((b) => h('option', { value: b.id, ...(b.id === board ? { selected: true } : {}) }, b.label)))),
  ) as HTMLSelectElement;
  const periodSel = h(
    'select',
    { 'aria-label': 'Period', 'data-lb': 'period' },
    h('option', { value: '' }, 'All time'),
    h('optgroup', { label: 'Gained in a quarter' }, ...idx.periods.filter((p) => p.kind === 'quarter').map((p) => h('option', { value: p.id }, p.label))),
    h('optgroup', { label: 'Gained during Torn’s events' }, ...idx.periods.filter((p) => p.kind === 'event').map((p) => h('option', { value: p.id }, p.label))),
  ) as HTMLSelectElement;
  periodSel.value = period;
  const factionInput = h('input', { type: 'search', inputmode: 'numeric', placeholder: 'Faction id', class: 'filter', 'aria-label': 'Faction id', value: faction }) as HTMLInputElement;
  const factionForm = h('form', { class: 'toolbar', 'data-lb': 'faction' }, factionInput, h('button', { type: 'submit', class: 'btn-small' }, 'Filter'), h('button', { type: 'button', class: 'btn-small', onclick: () => ((factionInput.value = ''), (faction = ''), void draw()) }, 'All factions'));
  factionForm.addEventListener('submit', (e) => {
    e.preventDefault();
    faction = factionInput.value.replace(/\D/g, '');
    void draw();
  });
  const out = h('div', { 'data-lb': 'out' });
  const status = h('p', { class: 'muted small', role: 'status', 'data-lb': 'status' });

  let drawing = 0;
  const draw = async () => {
    const mine = ++drawing;
    const def = leaderboardDef(board);
    const isStat = def?.kind === 'stat';
    const isFaction = def?.kind === 'faction';
    if (!isStat) period = '';
    periodSel.value = period;
    periodSel.disabled = !isStat;
    factionInput.disabled = isFaction;
    const params = new URLSearchParams({ board, ...(period ? { period } : {}), ...(faction && !isFaction ? { faction } : {}) });
    history.replaceState(history.state, '', `${location.pathname}?${params}`);
    out.replaceChildren(loading('Reading the board…'));
    let a: LeaderboardAnswer;
    try {
      const api = new URLSearchParams({ ...(period ? { period } : {}), ...(faction && !isFaction ? { faction } : {}) });
      a = await server<LeaderboardAnswer>('GET', `/api/leaderboards/${board}${api.toString() ? `?${api}` : ''}`);
    } catch (err) {
      if (mine === drawing) out.replaceChildren(problem(err));
      return;
    }
    if (mine !== drawing) return;
    const label = def?.label ?? board;
    const gains = !!a.period;
    const cols: Parameters<typeof table<LeaderboardRow>>[1] = isFaction
      ? [
          { label: '#', get: (r) => num(r.rank), sort: (r) => r.rank, cls: 'r' },
          { label: 'Faction', get: (r) => factionCell(r.id, r.name), sort: (r) => (r.name ?? '').toLowerCase() },
          { label: board === 'f-estimate' ? 'Members with an estimate' : 'Members', get: (r) => num(board === 'f-estimate' ? r.covered : r.members), sort: (r) => (board === 'f-estimate' ? r.covered : r.members) ?? 0, cls: 'r' },
          { label: board === 'f-estimate' ? 'Estimated total' : label.replace(/^Factions: (.)/, (_m, c: string) => c.toUpperCase()), get: (r) => valueText(board, r.value), sort: (r) => r.value, cls: 'r' },
          { label: 'From', get: (r) => (r.src === 'hof' ? `Torn’s Hall of Fame${r.place ? ` #${num(r.place)}` : ''}` : 'Omni-Torn’s estimates') },
        ]
      : [
          { label: '#', get: (r) => num(r.rank), sort: (r) => r.rank, cls: 'r' },
          { label: 'Player', get: playerCell, sort: (r) => (r.name ?? '').toLowerCase() },
          { label: 'Level', get: (r) => num(r.level), sort: (r) => r.level ?? 0, cls: 'r' },
          { label: 'Faction', get: (r) => factionCell(r.factionId), sort: (r) => r.factionId ?? 0 },
          ...(gains ? [{ label: 'At the start', get: (r: LeaderboardRow) => valueText(board, r.start ?? 0), sort: (r: LeaderboardRow) => r.start ?? 0, cls: 'r' }] : []),
          { label: gains ? 'Gained' : def?.kind === 'activity' ? 'Active (7 days)' : label, get: (r) => (gains ? `+${valueText(board, r.value)}` : valueText(board, r.value)), sort: (r) => r.value, cls: 'r' },
          { label: 'From', get: (r) => (gains ? `${a.fromDay ? new Date(a.fromDay * 86_400_000).toISOString().slice(0, 10) : '?'} → ${a.toDay ? new Date(a.toDay * 86_400_000).toISOString().slice(0, 10) : '?'}` : fromText(r)) },
        ];
    const empty = a.builtAt === null ? 'Not built yet — the leaderboards are put together once a day; look again later.' : faction ? 'Nobody from that faction on this board.' : gains ? 'Nobody gained on this board in that period yet.' : 'Nobody on this board yet.';
    out.replaceChildren(
      h('div', { 'data-lb-board': board }, table(a.rows, cols, { initial: 0, desc: false, empty })),
      a.note ? h('p', { class: 'muted small', 'data-lb': 'note' }, a.note) : '',
      a.total > a.rows.length ? h('p', { class: 'muted small' }, `The top ${num(LEADERBOARD_SIZE)} of ${num(a.total)}.`) : '',
    );
    status.textContent = `${a.builtAt ? `Built ${ago(a.builtAt)}` : 'Not built yet'}${idx.tornAt ? ` · Torn’s lists read ${ago(idx.tornAt)}` : ''}.`;
  };
  boardSel.addEventListener('change', () => ((board = boardSel.value), void draw()));
  periodSel.addEventListener('change', () => ((period = periodSel.value), void draw()));

  body.replaceChildren(
    box('Leaderboards', [h('div', { class: 'toolbar' }, boardSel, periodSel), factionForm, out, status], { id: 'leaderboard' }),
    box(
      'Where the numbers come from',
      [
        h('p', {}, 'Battle stats: Omni-Torn’s own estimate of each player — the same one the player page shows — rounded to 2 figures; estimates from rank alone are left out. Never anyone’s battle stats themselves.'),
        h('p', {}, `Public stats: Torn’s Hall of Fame (its top 100, read once a day) and the public personal stats Omni-Torn users on terms ${LEADERBOARD_TERMS} or later saw — so players nobody looked at aren’t here, and a number is as old as it says.`),
        h('p', {}, 'Factions: Torn’s faction Hall of Fame, and Omni-Torn’s estimates of each faction’s members added up (only the members Omni-Torn has an estimate of). Activity: the hours a player was active in the last 7 days, as Omni-Torn saw them (players it saw little of rank lower).'),
        h('p', {}, `Quarterly and event boards: what each public-stats board’s players gained between Omni-Torn’s daily snapshots (kept with no end date). A faction filter shows that faction’s players among each board’s top ${num(LEADERBOARD_KEEP)}.`),
      ],
      { id: 'leaderboard-about' },
    ),
  );
  await draw();
}
